import { useEffect, useRef, useState } from 'react';
import {
  advanceFill,
  createFillMotion,
  fillHasSettled,
  retargetFill,
  type FillMotion,
} from './fillMomentum';

/**
 * Drives the complexity bar's fill toward `target` (MUS-461).
 *
 * A frame loop over {@link advanceFill}, and nothing more: the motion itself —
 * the constant-rate lag, the momentum on a sustained climb and the ease into
 * the target — lives in `fillMomentum.ts`, which is pure and testable without a
 * DOM. This owns only the clock.
 *
 * It replaces the fixed-duration `useTween` the fill used to run on, which
 * re-eased from wherever it sat toward each new value over 800ms. That hook
 * still drives the paused/playing choreography, where a fixed duration is
 * exactly right; a lagging fill is a different problem and wanted its own
 * driver rather than another parameter on that one.
 *
 * `streakBoost` gates the momentum, per the design: with it off the fill still
 * lags at the base rate but never speeds up. It is read inside the loop rather
 * than depended on, so toggling it mid-climb takes effect on the next frame
 * without restarting the travel.
 *
 * Respects `prefers-reduced-motion` by snapping straight to the target.
 */
export function useMomentumFill(target: number, streakBoost: boolean): number {
  const [value, setValue] = useState(target);
  const motionRef = useRef<FillMotion>(createFillMotion(target));
  const streakBoostRef = useRef(streakBoost);
  /**
   * When the fill last arrived, so a climb that has been parked long enough to
   * count as a plateau does not lend its momentum to the next step. Null while
   * travelling.
   */
  const settledAtRef = useRef<number | null>(null);

  useEffect(() => {
    streakBoostRef.current = streakBoost;
  }, [streakBoost]);

  useEffect(() => {
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const now = performance.now();
    const idleMs = settledAtRef.current === null ? 0 : now - settledAtRef.current;
    motionRef.current = retargetFill(motionRef.current, target, idleMs);

    const settle = (at: number) => {
      motionRef.current = { ...motionRef.current, value: motionRef.current.target };
      settledAtRef.current = at;
      setValue(motionRef.current.target);
    };

    if (reducedMotion || fillHasSettled(motionRef.current)) {
      settle(now);
      return;
    }

    settledAtRef.current = null;
    let previousFrameAt: number | null = null;
    let frame = 0;

    const step = (frameAt: number) => {
      // The first frame carries no elapsed time of its own; it only establishes
      // where the clock is, so the travel starts from a known instant rather
      // than from however long the browser took to schedule it.
      const elapsedMs = previousFrameAt === null ? 0 : frameAt - previousFrameAt;
      previousFrameAt = frameAt;

      motionRef.current = advanceFill(motionRef.current, elapsedMs, streakBoostRef.current);
      setValue(motionRef.current.value);

      if (fillHasSettled(motionRef.current)) {
        settle(frameAt);
        return;
      }
      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return value;
}
